/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import api, { API } from '@/lib/api';

/** Fetch the preview of the next invoice number for the current year. */
export const getNextInvoiceNumber = async () =>
  (await api.get('/invoicing/next-number')).data;

/** Create a new invoice. Returns the persisted document (no PDF blob). */
export const createInvoice = async (payload) =>
  (await api.post('/invoicing/create', payload)).data;

/** List invoices for the current user. */
export const listInvoices = async ({ year = null, limit = 50, skip = 0 } = {}) => {
  const params = new URLSearchParams();
  if (year) params.set('year', String(year));
  params.set('limit', String(limit));
  params.set('skip', String(skip));
  return (await api.get(`/invoicing/list?${params.toString()}`)).data;
};

/** Fetch a single invoice detail. */
export const getInvoice = async (invoiceId) =>
  (await api.get(`/invoicing/${invoiceId}`)).data;

/** Return the direct PDF download URL (browser-safe, uses cookie auth). */
export const invoicePdfUrl = (invoiceId) => `${API}/invoicing/${invoiceId}/pdf`;

/** Return the FatturaPA XML download URL for SDI submission. */
export const invoiceXmlUrl = (invoiceId) => `${API}/invoicing/${invoiceId}/xml`;

/** Return the server-side CSV export URL (single-click download for the accountant). */
export const invoicesCsvExportUrl = ({ year = null, includeCancelled = false } = {}) => {
  const params = new URLSearchParams();
  if (year) params.set('year', String(year));
  if (includeCancelled) params.set('include_cancelled', 'true');
  const qs = params.toString();
  return `${API}/invoicing/export/csv${qs ? `?${qs}` : ''}`;
};

/** Send an already-issued invoice to SDI via configured PEC (returns transport id). */
export const sendInvoiceToSdi = async (invoiceId) =>
  (await api.post(`/invoicing/${invoiceId}/send-sdi`)).data;

/** Probe backend for PEC configuration (used to gate the "Invia SDI" button). */
export const getPecConfigStatus = async () =>
  (await api.get('/invoicing/pec-config')).data;

/** Fetch revenue analytics for the current professional user. */
export const getRevenueAnalytics = async ({ period = 'monthly', year = null } = {}) => {
  const params = new URLSearchParams();
  params.set('period', period);
  if (year) params.set('year', String(year));
  return (await api.get(`/invoicing/analytics/revenue?${params.toString()}`)).data;
};

/** Cancel an invoice (soft delete). */
export const cancelInvoice = async (invoiceId) =>
  (await api.delete(`/invoicing/${invoiceId}`)).data;
